import { cloneElement, isValidElement, useId, type ReactNode } from 'react'
import { SWATCHES } from '../lib/types'

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className="toggle"
      onClick={() => onChange(!checked)}
    >
      <span />
    </button>
  )
}

export function Dot({ color, large }: { color: string; large?: boolean }) {
  return <span className={large ? 'dot dot--lg' : 'dot'} style={{ background: color }} aria-hidden />
}

export function Bar({ pct, color }: { pct: number; color: string }) {
  return (
    <div className="track">
      <div style={{ width: `${Math.max(2, Math.min(100, pct))}%`, background: color }} />
    </div>
  )
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string
  body: string
  action?: { label: string; onClick: () => void }
}) {
  return (
    <div className="empty">
      {/* h2, not h3: the page's own h1 is the only heading above this,
          and skipping a level makes heading navigation lie about nesting. */}
      <h2>{title}</h2>
      <p className="pretty">{body}</p>
      {action && (
        <button type="button" className="btn btn--primary" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  )
}

export function SwatchPicker({
  value,
  onChange,
  colors = SWATCHES as readonly string[],
  label = 'Colour',
}: {
  value: string
  onChange: (c: string) => void
  colors?: readonly string[]
  label?: string
}) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} role="radiogroup" aria-label={label}>
      {colors.map((c) => (
        <button
          key={c}
          type="button"
          role="radio"
          aria-checked={value === c}
          aria-label={c}
          className="swatch"
          onClick={() => onChange(c)}
          style={{
            background: c,
            boxShadow: value === c ? `0 0 0 2px var(--surf), 0 0 0 4px ${c}` : 'none',
          }}
        />
      ))}
    </div>
  )
}

/**
 * A labelled form row.
 *
 * The label used to be a bare <span>, associated with nothing. Every field in
 * every create and edit dialog in the app goes through here, so a screen
 * reader announced the lot as "edit text, blank" -- the name was on screen
 * and not in the accessibility tree.
 *
 * The id is generated and cloned onto the child, so the ~25 call sites did
 * not each have to invent one and keep the two spellings in step. A caller
 * that sets its own id keeps it.
 */
export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  const generated = useId()
  const child = isValidElement<{ id?: string }>(children)
    ? cloneElement(children, { id: children.props.id ?? generated })
    : children
  const htmlFor = isValidElement<{ id?: string }>(children)
    ? (children.props.id ?? generated)
    : undefined

  return (
    <div>
      <label className="label" htmlFor={htmlFor}>
        {label}
      </label>
      {child}
      {hint && (
        <div style={{ fontSize: 12, color: 'var(--faint)', marginTop: 5 }} className="pretty">
          {hint}
        </div>
      )}
    </div>
  )
}

export function ChipRow<T extends string>({
  options,
  value,
  onChange,
  accent,
  label,
}: {
  options: readonly T[]
  value: T
  onChange: (v: T) => void
  accent?: boolean
  label: string
}) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          className={accent ? 'chip chip--ac' : 'chip'}
          aria-pressed={value === o}
          onClick={() => onChange(o)}
        >
          {o}
        </button>
      ))}
    </div>
  )
}
